import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useRole } from '../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faRefresh,
  faTrash,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import { AdminGuard } from '../components/RoleGuards';
import LocationsTable from '../components/locations/LocationsTable';
import LocationForm from '../components/locations/LocationForm';
import LocationDetails from '../components/locations/LocationDetails';
import ActionToolbar from '../components/common/ActionToolbar';
import ConfirmDialog from '../components/common/ConfirmDialog';
import MessageAlert from '../components/common/MessageAlert';
import useLocations from '../hooks/useLocations';
// do not call states API from this page; keep states empty to avoid network calls
import { UI_COLORS } from '../utils/colors';
import { useErrorDialog } from '../context/errorDialogContext';
import { useTranslation } from 'react-i18next';

/**
 * LocationsPage Component
 * - Main page for managing locations with full CRUD operations
 * - Includes search, filtering, bulk operations, and export functionality
 * - Restricted to Super Admin access only
 */
const LocationsPage = () => {
  const { t } = useTranslation();
  const strings = t('locations', { returnObjects: true });
  const { showError } = useErrorDialog();

  // Location management hook
  const {
    locations,
    loading,
    error,
    pagination,
    filters,
    sortConfig,
    selectedLocationIds,
    fetchLocations,
    getLocationById,
    createLocation,
    updateLocation,
    deleteLocation,
    bulkDeleteLocations,
    updateSort,
    updateFilters,
    goToPage,
    nextPage,
    prevPage,
    selectLocation,
    selectAllLocations,
    clearSelection,
    isLocationSelected,
    isAllSelected,
    hasSelection,
    selectionCount,
    hasLocations,
    clearError,
  } = useLocations();

  const searchDebounceRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { checkDeleteAccess } = useRole();

  // Use states from en.js strings — no API call needed
  const states = useMemo(() => strings.states || [], [strings.states]);

  // UI-only filters (mirrors customer filter pattern)
  const [uiFilters, setUiFilters] = useState({
    search: filters?.search ?? '',
    state: filters?.state ?? '',
  });

  // Keep UI filters in sync with hook filters
  React.useEffect(() => {
    const nextFilters = {
      search: filters?.search ?? '',
      state: filters?.state ?? '',
    };

    setUiFilters(prev => {
      if (prev.search === nextFilters.search && prev.state === nextFilters.state) {
        return prev;
      }
      return nextFilters;
    });
  }, [filters]);

  // Local UI state
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [viewingLocation, setViewingLocation] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Message alerts
  const [messageAlert, setMessageAlert] = useState(null);

  /**
   * Display error messages via global error dialog (ErrorDialogProvider)
   */
  React.useEffect(() => {
    if (error) {
      const message = typeof error === 'string'
        ? error
        : (error?.detail || error?.message || (typeof error === 'object' ? JSON.stringify(error) : String(error)));

      showError(message, strings.page?.alerts?.errorTitle);
      // Clear once shown: `error` sticks until the next request, and this effect re-runs on
      // every render because `strings.page` is a fresh object each time (t(..., { returnObjects:
      // true }) does not memoize). Leaving it set re-opens the dialog the operator just
      // dismissed, which reads as a frozen screen.
      clearError();
    }
  }, [error, showError, clearError, strings.page]);

  /**
   * Show success message alert
   * @param {string} message - Success message
   */
  const showSuccessMessage = useCallback((message) => {
    setMessageAlert({
      type: 'success',
      message,
      onClose: () => setMessageAlert(null),
    });
  }, []);

  /**
   * Show error message alert
   * @param {string} message - Error message
   */
  const showErrorMessage = useCallback((message) => {
    setMessageAlert({
      type: 'error',
      message,
      onClose: () => setMessageAlert(null),
    });
  }, []);

  /**
   * Handle refresh locations
   */
  const handleRefresh = useCallback(() => {
    (async () => {
      try {
        await fetchLocations({ force: true });
        showSuccessMessage(strings.messages.refreshSuccess);
      } catch (err) {
        showErrorMessage(err?.message || strings.messages.refreshError);
      }
    })();
  }, [
    fetchLocations,
    showSuccessMessage,
    showErrorMessage,
    strings.messages.refreshSuccess,
    strings.messages.refreshError,
  ]);

  /**
   * Handle search input change
   * @param {string} searchTerm - Search term
   */
  const handleSearch = useCallback((searchTerm) => {
    setSearchQuery(searchTerm);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateFilters({ search: searchTerm });
    }, 400);
  }, [updateFilters]);

  /**
   * Handle filter change by key
   * @param {string} key - Filter key
   * @param {string} value - Filter value
   */
  const handleFilterChange = useCallback((key, value) => {
    setUiFilters(prev => ({ ...prev, [key]: value }));
    updateFilters({ [key]: value });
  }, [updateFilters]);


  /**
   * Open form for creating a new location
   */
  const openCreateForm = useCallback(() => {
    setEditingLocation(null);
    setShowForm(true);
  }, []);

  /**
   * Open form for editing a location
   * @param {string|number} locationId - Location ID
   */
  const openEditForm = useCallback(async (locationId) => {
    setActionLoading(true);
    try {
      const location = await getLocationById(locationId, { withLoading: false });
      if (location) {
        setEditingLocation(location);
        setShowForm(true);
      } else {
        showErrorMessage(strings.messages.fetchError);
      }
    } catch (error) {
      showErrorMessage(error.message || strings.messages.fetchError);
    } finally {
      setActionLoading(false);
    }
  }, [getLocationById, showErrorMessage, strings.messages.fetchError]);

  /**
   * Handle form submission (create or update)
   * @param {Object} locationData - Location form data
   */
  const handleFormSubmit = useCallback(async (locationData) => {
    setFormLoading(true);

    try {
      let result;
      if (editingLocation) {
        result = await updateLocation(editingLocation.id, locationData);
        if (result.success) {
          showSuccessMessage(strings.messages.updateSuccess);
          setShowForm(false);
          setEditingLocation(null);
        } else {
          showErrorMessage(result.error || strings.messages.updateError);
        }
      } else {
        result = await createLocation(locationData);
        if (result.success) {
          showSuccessMessage(strings.messages.createSuccess);
          setShowForm(false);
        } else {
          showErrorMessage(result.error || strings.messages.createError);
        }
      }
      return result;
    } catch (error) {
      showErrorMessage(error.message || strings.messages.createError);
      return { success: false, error: error.message };
    } finally {
      setFormLoading(false);
    }
  }, [
    editingLocation,
    createLocation,
    updateLocation,
    showSuccessMessage,
    showErrorMessage,
    strings.messages,
  ]);

  /**
   * Open location details modal
   * @param {string|number} locationId - Location ID
   */
  const openDetails = useCallback(async (locationId) => {
    setDetailsLoading(true);
    setViewingLocation(null);
    setShowDetails(true);

    try {
      const location = await getLocationById(locationId, { withLoading: false });
      if (location) {
        setViewingLocation(location);
      } else {
        showErrorMessage(strings.messages.fetchError);
        setShowDetails(false);
      }
    } catch (error) {
      showErrorMessage(error.message || strings.messages.fetchError);
      setShowDetails(false);
    } finally {
      setDetailsLoading(false);
    }
  }, [getLocationById, showErrorMessage, strings.messages.fetchError]);

  /**
   * Open delete confirmation for single location
   * @param {string|number} locationId - Location ID
   */
  const openDeleteConfirm = useCallback((locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    const locationName = location?.name || strings.table.copy.unnamedLocation;

    setConfirmDialog({
      type: 'delete-single',
      title: strings.confirm.deleteSingle.title,
      message: strings.confirm.deleteSingle.message,
      confirmLabel: strings.confirm.deleteSingle.confirmLabel,
      loadingLabel: strings.confirm.deleteSingle.loadingLabel,
      confirmVariant: 'danger',
      locationId,
      locationName,
    });
  }, [locations, strings.confirm.deleteSingle, strings.table.copy.unnamedLocation]);

  /**
   * Open bulk delete confirmation
   */
  const openBulkDeleteConfirm = useCallback(() => {
    const message = selectionCount === 1
      ? strings.confirm.deleteBulk.messageSingle
      : strings.confirm.deleteBulk.messageMultiple.replace('{count}', selectionCount);

    setConfirmDialog({
      type: 'delete-bulk',
      title: strings.confirm.deleteBulk.title,
      message,
      confirmLabel: strings.confirm.deleteBulk.confirmLabel,
      loadingLabel: strings.confirm.deleteBulk.loadingLabel,
      confirmVariant: 'danger',
      count: selectionCount,
    });
  }, [selectionCount, strings.confirm.deleteBulk]);



  /**
   * Execute confirmed action
   */
  const handleConfirm = useCallback(async () => {
    if (!confirmDialog) return;

    setConfirmLoading(true);

    try {
      let result;

      switch (confirmDialog.type) {
        case 'delete-single':
          result = await deleteLocation(confirmDialog.locationId);
          if (result.success) {
            showSuccessMessage(strings.messages.deleteSuccess);
          } else {
            showErrorMessage(result.error || strings.messages.deleteError);
          }
          break;

        case 'delete-bulk':
          result = await bulkDeleteLocations();
          if (result.success) {
            showSuccessMessage(strings.messages.bulkDeleteSuccess);
          } else {
            showErrorMessage(result.error || strings.messages.bulkDeleteError);
          }
          break;


        default:
          break;
      }

      setConfirmDialog(null);
    } catch (error) {
      showErrorMessage(error.message);
    } finally {
      setConfirmLoading(false);
    }
  }, [
    confirmDialog,
    deleteLocation,
    bulkDeleteLocations,
    showSuccessMessage,
    showErrorMessage,
    strings.messages,
  ]);


  /**
   * Get computed displayed locations (for display purposes only - actual filtering happens in hook)
   */
  const displayedLocations = useMemo(() => {
    if (!Array.isArray(locations)) return [];
    return locations;
  }, [locations]);

  const filterElements = useMemo(() => {
    const stateOptions = (states || []).map((state) => {
      const label = typeof state === 'string' ? state : state.label;
      const key = typeof state === 'string' ? state : state.value;
      if (!label) return null;
      return (
        <option key={key} value={label} style={{ color: UI_COLORS.TEXT.BLACK }}>
          {label}
        </option>
      );
    }).filter(Boolean);

    return [
      <div key="state" className="flex items-center gap-2">
        <FontAwesomeIcon icon={faFilter} style={{ color: UI_COLORS.TEXT.MUTED }} />
        <select
          value={uiFilters.state}
          onChange={(event) => handleFilterChange('state', event.target.value)}
          className="rounded-xl border px-3 py-2 text-sm"
          style={{
            backgroundColor: UI_COLORS.FORMS.INPUT.background,
            borderColor: UI_COLORS.FORMS.INPUT.border,
            color: UI_COLORS.TEXT.BLACK,
          }}
          aria-label={strings.filters.state}
        >
          <option value="" style={{ color: UI_COLORS.TEXT.BLACK }}>
            {strings.filters.all}
          </option>
          {stateOptions}
        </select>
      </div>,
    ];
  }, [states, uiFilters.state, handleFilterChange, strings.filters.all, strings.filters.state]);

  /**
   * Define actions for ActionToolbar
   */
  const toolbarActions = useMemo(() => {
    const actions = [
      {
        key: 'refresh',
        icon: faRefresh,
        onClick: handleRefresh,
        disabled: loading,
        variant: 'secondary',
        tooltip: strings.actions.buttons.refresh,
        spinner: loading,
        ariaLabel: strings.actions.buttons.refresh,
      },
      // export action removed per request
    ];

    // Bulk delete (only show when selections exist and user has delete access)
    if (hasSelection && checkDeleteAccess()) {
      actions.push({
        key: 'bulk-delete',
        icon: faTrash,
        label: `${strings.actions.buttons.bulkDelete} (${selectionCount})`,
        onClick: openBulkDeleteConfirm,
        disabled: loading,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    // Add location button
    actions.push({
      key: 'add-location',
      icon: faPlus,
      label: strings.actions.buttons.add,
      onClick: openCreateForm,
      disabled: loading,
      variant: 'primary',
      showLabelBreakpoint: 'sm',
    });

    return actions;
  }, [
    loading,
    hasSelection,
    selectionCount,
    handleRefresh,
    openBulkDeleteConfirm,
    openCreateForm,
    strings.actions.buttons,
    checkDeleteAccess,
  ]);

  return (
    <AdminGuard
      fallback={
        <div className="p-6">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2" style={{ color: UI_COLORS.TEXT.BLACK }}>
              {strings.page.accessDenied.title}
            </h2>
            <p style={{ color: UI_COLORS.TEXT.BLACK }}>{strings.page.accessDenied.message}</p>
          </div>
        </div>
      }
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.BLACK }}>
            {strings.page.header.title}
          </h1>
          <p className="mt-1" style={{ color: UI_COLORS.TEXT.BLACK }}>
            {strings.page.header.subtitle}
          </p>

          {/* Stats */}
          <div className="mt-4 flex space-x-4">
            <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.BLACK }}>{strings.page.stats.total}</div>
                <div className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.BLACK }}>{pagination.totalCount}</div>
              </div>
            </div>
            {hasSelection && (
              <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <div className="text-sm" style={{ color: UI_COLORS.TEXT.BLACK }}>{strings.page.stats.selected}</div>
                  <div className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.BLACK }}>{selectionCount}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Toolbar */}
        <ActionToolbar
          searchValue={searchQuery}
          onSearchChange={handleSearch}
          searchPlaceholder={strings.actions.searchPlaceholder}
          filters={filterElements}
          actions={toolbarActions}
          className="mb-6"
        />

        {/* Table */}
        <div className="bg-white rounded-lg border">
          <LocationsTable
            locations={displayedLocations}
            loading={loading}
            selectedLocationIds={selectedLocationIds}
            sortConfig={sortConfig}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            onSort={updateSort}
            onSelectLocation={selectLocation}
            onSelectAll={selectAllLocations}
            onClearSelection={clearSelection}
            onEditLocation={openEditForm}
            onDeleteLocation={openDeleteConfirm}
            onViewLocation={openDetails}
            onPageChange={goToPage}
            onNextPage={nextPage}
            onPrevPage={prevPage}
            isLocationSelected={isLocationSelected}
            isAllSelected={isAllSelected}
            hasLocations={hasLocations}
            actionLoading={actionLoading}
          />
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div
            className="mt-4 flex items-center justify-between rounded-xl border px-6 py-3"
            style={{ borderColor: 'rgba(0,0,0,0.08)', backgroundColor: '#fff' }}
          >
            <span className="text-sm" style={{ color: '#6b7280' }}>
              Page {pagination.currentPage} of {pagination.totalPages} — {pagination.totalCount} total
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prevPage}
                disabled={pagination.currentPage <= 1 || loading}
                className="rounded-lg border px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#6b7280' }}
              >
                Previous
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - pagination.currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                  acc.push(p);
                  return acc;
                }, [])
                .map((item) =>
                  typeof item === 'string' ? (
                    <span key={item} className="px-2 text-sm" style={{ color: '#9ca3af' }}>...</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => goToPage(item)}
                      disabled={loading}
                      className="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: item === pagination.currentPage ? '#0066cc' : 'transparent',
                        color: item === pagination.currentPage ? '#fff' : '#374151',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                type="button"
                onClick={nextPage}
                disabled={pagination.currentPage >= pagination.totalPages || loading}
                className="rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#0066cc', color: '#fff' }}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Location Form Modal */}
        <LocationForm
          isOpen={showForm}
          onClose={() => {
            setShowForm(false);
            setEditingLocation(null);
          }}
          onSubmit={handleFormSubmit}
          location={editingLocation}
          loading={formLoading}
        />

        {/* Location Details Modal */}
        <LocationDetails
          isOpen={showDetails}
          onClose={() => {
            setShowDetails(false);
            setViewingLocation(null);
          }}
          location={viewingLocation}
          loading={detailsLoading}
        />

        {/* Confirmation Dialog */}
        {confirmDialog && (
          <ConfirmDialog
            isOpen={true}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            cancelLabel={confirmDialog.cancelLabel ?? strings.confirm.buttons.cancel}
            confirmVariant={confirmDialog.confirmVariant}
            loading={confirmLoading}
            loadingLabel={confirmDialog.loadingLabel}
            onConfirm={() => {
              if (confirmDialog.type === 'error') {
                // clear the hook error and close the dialog
                clearError();
                setConfirmDialog(null);
                return;
              }
              handleConfirm();
            }}
            onCancel={() => {
              if (confirmDialog.type === 'error') {
                clearError();
              }
              setConfirmDialog(null);
            }}
          />
        )}

        {/* Message Alert */}
        {messageAlert && (
          <MessageAlert
            type={messageAlert.type}
            message={messageAlert.message}
            onClose={messageAlert.onClose}
            autoClose={true}
            duration={4000}
          />
        )}
      </div>
    </AdminGuard>
  );
};

export default LocationsPage;